import { useQuery } from '@tanstack/react-query';
import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { AgendaOrden, DiaEvaluado, OrdenDetalle, VeredictoFecha } from '@manejo-alcanza/contracts';
import { agendaDeOrden, fecha } from '../api';
import { Card } from './basicos';

/**
 * F2 — qué día cabe esta entrega.
 *
 * El calendario del CEDI dice, por almacén, los días en que **recibe de
 * nosotros**. Esta tarjeta lo cruza con la fecha que pide la orden.
 *
 * **La respuesta es por tienda, no por orden.** Una orden de cross-docking va a
 * muchas tiendas en un solo viaje y no todas reciben los mismos días, así que un
 * «sí» o «no» para la orden entera tendría que mentir en alguno de los dos
 * sentidos. Cuando unas reciben y otras no, la tarjeta lo dice y muestra cuáles.
 *
 * **Y dice «no sé» cuando no sabe.** Unos 190 almacenes del calendario no traen
 * día —viene de un impreso de 2023—, y una tienda sin frecuencia **no cuenta
 * como que no recibe**. Contarla así convertiría un hueco nuestro en una
 * acusación al cliente.
 */
export function PanelAgenda({ orden }: { orden: OrdenDetalle }): ReactNode {
  const consulta = useQuery({
    queryKey: ['agenda', orden.id],
    queryFn: () => agendaDeOrden(orden.id),
  });

  // Una orden anulada no se va a entregar: preguntarle al calendario por ella
  // es ruido en la pantalla más cargada del sistema.
  if (orden.estado === 'ANULADA') return null;
  if (consulta.isLoading || consulta.isError) return null;

  const a = consulta.data!;

  if (!a.aplicaCalendario) {
    return (
      <Card titulo="Qué día cabe">
        <p className="text-sm text-tenue">
          {orden.clienteNombre} recibe <strong>en la tienda</strong>, no en un CEDI, y esta
          orden trae una ventana de{' '}
          {dias(a.ventanaDesde, a.ventanaHasta)} días: del {fecha(a.ventanaDesde)} al{' '}
          {fecha(a.ventanaHasta)}. El calendario del CEDI no aplica aquí.
        </p>
        {/* Del cliente no sabemos, pero de nuestro camión sí: media respuesta
            es mejor que ninguna, y es la mitad que depende de nosotros. */}
        <div className="mt-2">
          <ElCamion dia={a.pedida} />
        </div>
      </Card>
    );
  }

  return (
    <Card titulo="Qué día cabe">
      <Veredicto agenda={a} />

      {a.destinos.length > 0 && <Destinos agenda={a} />}

      {a.proximas.length > 0 && (
        <div className="mt-4">
          <h4 className="mb-1.5 text-sm font-medium">Los próximos días que caben</h4>
          <ul className="flex flex-wrap gap-2">
            {a.proximas.map((d) => (
              <li
                key={d.fecha}
                className="rounded border border-borde px-2 py-1 text-xs"
                title={
                  d.veredicto === 'PARCIAL'
                    ? `${d.reciben} de ${d.reciben + d.noReciben} tiendas conocidas`
                    : 'todas las tiendas que se conocen reciben'
                }
              >
                <span className="font-medium">{nombreDelDia(d.diaSemana)}</span>{' '}
                {fecha(d.fecha)}
                {d.veredicto === 'PARCIAL' && (
                  <span className="text-tenue"> · {d.reciben} de {d.reciben + d.noReciben}</span>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-tenue">
            La cita se pide en C3. Esto dice qué día pedirla, no la reserva.
          </p>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

function Veredicto({ agenda: a }: { agenda: AgendaOrden }): ReactNode {
  const d = a.pedida;
  const conocidas = d.reciben + d.noReciben;

  const texto: Record<VeredictoFecha, ReactNode> = {
    CABE: (
      <>
        El CEDI recibe el <strong>{nombreDelDia(d.diaSemana).toLowerCase()}</strong> para las{' '}
        {conocidas === 1 ? 'la tienda' : `${conocidas} tiendas`} de esta orden que el calendario
        conoce.
      </>
    ),
    NO_CABE: (
      <>
        El CEDI <strong>no recibe el {nombreDelDia(d.diaSemana).toLowerCase()}</strong> para{' '}
        {conocidas === 1 ? 'la tienda' : `ninguna de las ${conocidas} tiendas`} de esta orden que el
        calendario conoce. La orden está pedida fuera de la frecuencia.
      </>
    ),
    PARCIAL: (
      <>
        El {nombreDelDia(d.diaSemana).toLowerCase()} sirve para <strong>{d.reciben}</strong> de las{' '}
        {conocidas} tiendas conocidas y <strong>no</strong> para las otras {d.noReciben}.{' '}
        <span className="text-tenue">Ningún día le sirve a todas.</span>
      </>
    ),
    NO_SE: (
      <>
        <strong>No se sabe.</strong> El calendario no trae la frecuencia de ninguna de las{' '}
        {d.noSe === 1 ? 'tienda' : `${d.noSe} tiendas`} de esta orden.
      </>
    ),
  };

  return (
    <div className="space-y-2">
      <div className={`rounded border-l-4 px-3 py-2 text-sm ${COLOR[d.veredicto]}`}>
        <div className="text-xs uppercase tracking-wide text-tenue">
          Pedida para el {fecha(d.fecha)}
        </div>
        <p className="mt-0.5">{texto[d.veredicto]}</p>
      </div>

      <ElCamion dia={d} />

      {d.noSe > 0 && d.veredicto !== 'NO_SE' && (
        <p className="text-xs text-tenue">
          De otras {d.noSe} tiendas el calendario no dice nada: no cuentan ni a favor ni en
          contra.
          {a.sinResolver > 0 && ` ${a.sinResolver} todavía no se pudieron identificar.`}
        </p>
      )}

      {/*
        El puente con F3: llega al formulario con la orden ya elegida, que es
        donde mas facil se digita la orden equivocada.

        **El motivo no se rellena.** «Orden de compra fuera de frecuencia» es lo
        que el calendario sugiere, pero de el sale la responsabilidad, y esa la
        afirma una persona. Un motivo puesto solo, que ademas acusa al cliente,
        es exactamente el dato inventado que se ve correcto.
      */}
      {(d.veredicto === 'NO_CABE' || d.veredicto === 'PARCIAL') && (
        <p className="text-sm">
          <Link className="underline underline-offset-2" to={`/cau?orden=${a.ordenId}`}>
            Pedir una prórroga para esta orden
          </Link>{' '}
          <span className="text-tenue">— el motivo lo eliges tú.</span>
        </p>
      )}
    </div>
  );
}

/**
 * Si **nosotros** podemos salir ese día.
 *
 * Va aparte del veredicto del cliente a propósito: un día puede caber para el
 * CEDI y no para el camión, y quien lee esto necesita saber cuál de las dos
 * cosas resolver. Mezclarlas en un solo semáforo mandaría a pedir una prórroga
 * cuando el problema era que el camión estaba en el taller.
 */
function ElCamion({ dia }: { dia: DiaEvaluado }): ReactNode {
  if (!dia.camion) return null;

  if (dia.camion.estado === 'PUEDE') {
    return <p className="text-xs text-tenue">El camión sí puede salir ese día.</p>;
  }

  if (dia.camion.estado === 'NO_SE') {
    return (
      <p className="text-xs text-tenue">
        No se sabe si el camión puede salir: {dia.camion.detalle}{' '}
        <Link className="underline underline-offset-2" to="/camion">
          Digitarlo
        </Link>
      </p>
    );
  }

  return (
    <div className="rounded border-l-4 border-l-alerta bg-alerta-tenue px-3 py-2 text-sm">
      <div className="text-xs uppercase tracking-wide text-tenue">
        {dia.camion.razon === 'PICO_Y_PLACA' ? 'Pico y placa' : 'Sin camión'}
      </div>
      <p className="mt-0.5">
        <strong>El camión no puede salir ese día.</strong> {dia.camion.detalle}
      </p>
    </div>
  );
}

function Destinos({ agenda: a }: { agenda: AgendaOrden }): ReactNode {
  // Primero las que no reciben: son las que hay que mirar.
  const orden = { false: 0, true: 1, null: 2 } as const;
  const destinos = [...a.destinos].sort(
    (x, y) => orden[String(x.recibe) as keyof typeof orden] - orden[String(y.recibe) as keyof typeof orden],
  );
  const conocidas = destinos.filter((d) => d.recibe !== null);

  if (conocidas.length === 0) return null;

  return (
    <div className="mt-4">
      <h4 className="mb-1.5 text-sm font-medium">
        Las {conocidas.length} tiendas de las que se sabe la frecuencia
      </h4>
      <ul className="space-y-1 text-sm">
        {conocidas.map((d) => (
          <li key={`${d.codigo}-${d.nombre}`} className="flex flex-wrap items-baseline gap-2">
            <span className={d.recibe ? 'text-tenue' : 'font-medium'}>
              {d.recibe ? 'recibe' : 'no recibe'}
            </span>
            <span>
              {d.codigo && <span className="font-mono text-xs">{d.codigo}</span>} {d.nombre ?? ''}
            </span>
            <span className="text-xs text-tenue">{d.frecuencia}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Los tokens de docs/DISENO.md. Un color que no este ahi no se usa. */
const COLOR: Record<VeredictoFecha, string> = {
  CABE: 'border-l-acento bg-acento-tenue',
  PARCIAL: 'border-l-aviso bg-aviso-tenue',
  NO_CABE: 'border-l-alerta bg-alerta-tenue',
  // «No se» es neutro a proposito: no es una alarma, es un hueco del calendario.
  NO_SE: 'border-l-neutro bg-neutro-tenue',
};

const NOMBRES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

function nombreDelDia(diaSemana: DiaEvaluado['diaSemana']): string {
  return NOMBRES[diaSemana - 1] ?? '';
}

function dias(desde: string, hasta: string): number {
  return Math.round(
    (Date.parse(`${hasta}T00:00:00Z`) - Date.parse(`${desde}T00:00:00Z`)) / 86_400_000,
  );
}
